import type { LadooGame, LadooPlayer, PlayerColor, Token } from "./types.js";
import { ALL_COLORS, PLAYER_START } from "./types.js";

export function createGame(playerCount: number, cpuCount: number): LadooGame {
  const players: LadooPlayer[] = [];
  for (let i = 0; i < playerCount; i++) {
    players.push({
      color: ALL_COLORS[i],
      name: `Player ${i + 1}`,
      tokens: [tok(0), tok(1), tok(2), tok(3)],
      isCPU: i >= playerCount - cpuCount,
    });
  }
  return { players, turn: 0, dice: null, rolled: false, winner: null, sixCount: 0 };
}

function tok(id: number): Token {
  return { id, pos: -1 };
}

export function rollDice(): number {
  return Math.floor(Math.random() * 6) + 1;
}

// Get the absolute common-path position for a player-relative pos
export function toGlobalPos(player: PlayerColor, relPos: number): number {
  const start = PLAYER_START[player];
  if (relPos < 0 || relPos > 51) return -1;
  return (start + relPos) % 52;
}

// Can this token move `steps` from its current position?
export function canMoveToken(token: Token, steps: number): boolean {
  if (token.pos === 57) return false; // already home
  if (token.pos === -1) return steps === 6; // need 6 to leave base

  const newPos = token.pos + steps;
  if (newPos > 57) return false; // overshoot home
  return true;
}

// Apply a move. Returns new game state and info about captures.
export function applyMove(
  game: LadooGame,
  tokenIndex: number,
  steps: number
): { game: LadooGame; captured: number[]; landedHome: boolean } {
  const players = game.players.map((p) => ({ ...p, tokens: p.tokens.map((t) => ({ ...t })) }));
  const player = players[game.turn];
  const token = player.tokens[tokenIndex];

  const captured: number[] = [];
  let landedHome = false;

  if (token.pos === -1) {
    // Leaving base — place at start (position 0 on this player's path)
    token.pos = 0;
    // Check if another player's token is at this global position
    checkCapture(players, player.color, 0, captured);
  } else {
    const newPos = token.pos + steps;
    if (newPos === 57) {
      token.pos = 57;
      landedHome = true;
    } else if (newPos > 57) {
      // overshoot — bounce back? or just don't move. Let's not move.
      return { game, captured: [], landedHome: false };
    } else {
      token.pos = newPos;
      // Only check capture on common path (pos < 52)
      if (newPos < 52) {
        checkCapture(players, player.color, newPos, captured);
      }
    }
  }

  // Check win condition
  let winner = game.winner;
  if (player.tokens.every((t) => t.pos === 57)) {
    winner = game.turn;
  }

  // Handle sixes
  let sixCount = steps === 6 ? game.sixCount + 1 : 0;
  let turn = game.turn;
  let rolled = false;
  let dice: number | null = null;

  if (sixCount >= 3) {
    // Three consecutive sixes — lose turn
    sixCount = 0;
    turn = nextTurn(game.turn, players.length);
    rolled = false;
    dice = null;
  } else if (steps === 6) {
    // Gets another turn
    sixCount = sixCount;
    rolled = false;
    dice = null;
  } else {
    turn = nextTurn(game.turn, players.length);
    sixCount = 0;
    rolled = false;
    dice = null;
  }

  return {
    game: { players, turn, dice, rolled, winner, sixCount },
    captured,
    landedHome,
  };
}

function checkCapture(
  players: LadooPlayer[],
  movingColor: PlayerColor,
  relPos: number,
  captured: number[]
) {
  const globalPos = toGlobalPos(movingColor, relPos);
  for (const p of players) {
    if (p.color === movingColor) continue;
    // Check if opponent is on a safe zone
    for (const t of p.tokens) {
      if (t.pos >= 0 && t.pos < 52) {
        const oppGlobal = toGlobalPos(p.color, t.pos);
        if (oppGlobal === globalPos) {
          t.pos = -1; // send back to base
          captured.push(p.tokens.indexOf(t));
        }
      }
    }
  }
}

export function nextTurn(current: number, playerCount: number): number {
  return (current + 1) % playerCount;
}

// Get all valid moves for current player after a dice roll
export function getValidMoves(game: LadooGame): number[] {
  if (game.dice === null) return [];
  const player = game.players[game.turn];
  const moves: number[] = [];
  for (let i = 0; i < 4; i++) {
    if (canMoveToken(player.tokens[i], game.dice)) {
      moves.push(i);
    }
  }
  return moves;
}

// Check if player has any valid move
export function hasValidMove(game: LadooGame): boolean {
  return getValidMoves(game).length > 0;
}
